import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { Appointment } from '@shared/services/appointment/getLists/types/getAppointmentList.type';
import type { TodayAppointment } from '@shared/services/appointment/getLists/types/getTodayAppointments.types';
import type { AppointmentSystemParamsResult } from '@shared/services/appointment/makeNewNormal/types/getMessages.type';

import { initialState } from '@features/Appointment/stores/initialState';
import type {
  AppointmentDateRange,
  AppointmentSlot,
  CreateNewAppointment,
  GroupedAppointmentSlots,
  PreQuestionnaire,
  VcpPatientDetails,
} from '@features/Appointment/types/appointment.types';

const appointmentSlice = createSlice({
  initialState,
  name: 'Appointment',
  reducers: {
    clarMregSuccessErrText: (state) => {
      state.mregSuccessErrText = null;
    },
    clearAppointmentSlot: (state) => {
      state.selectedSlot = null;
    },
    clearAppointmentSystemParams: (state) => {
      state.systemParams = null;
    },
    clearAppointmentType: (state) => {
      state.appointmentType = null;
    },
    clearFinalConfirmMessage: (state) => {
      state.finalConfirmMessage = null;
    },
    // reset autogen make new
    resetAutogenMakeNew: (state) => {
      state.createNewAppointment.isAutogenMakeNew = false;
    },
    // reset autogen tcu
    resetAutogenSlots: (state) => {
      state.createNewAppointment.autogenBookedSlot = null;
      state.createNewAppointment.selectedTCUSlots = null;
      state.selectedSlot = null;
    },
    resetCreateNewState: (state) => {
      state.createNewAppointment.selectedLocation = null;
      state.createNewAppointment.selectedDateTime = null;
    },
    resetSelectedLocation: (state) => {
      state.createNewAppointment.selectedLocation = null;
    },
    resetAppointmentDateRange: (state) => {
      state.appointmentDateRange = null;
    },
    resetHsgTcuRequestId: (state) => {
      state.hsgTcuRequestId = null;
    },
    resetIsHSGAppointment: (state) => {
      state.createNewAppointment.isHSGAppointment = false;
    },
    resetLinkedSlots: (state) => {
      state.createNewAppointment.selectedLinkedSlots = null;
      state.selectedSlot = null;
    },
    resetMregTodayAppointment: (state) => {
      state.mregTodayAppointment = null;
    },
    resetSelectedAppointment: (state) => {
      state.selectedAppointment = initialState.selectedAppointment;
    },
    resetSelectedLinkedAppointment: (state) => {
      state.rescheduleAndBookNext.selectedLinkedAppointment =
        initialState.rescheduleAndBookNext.selectedLinkedAppointment;
    },

    /// Reset state
    resetStateAppointment: (state) => {
      state.createNewAppointment = initialState.createNewAppointment;
      state.rescheduleAndBookNext = initialState.rescheduleAndBookNext;
      state.selectedSlot = null;
      state.systemParams = null;
      state.appointmentDateRange = null;
      state.hsgTcuRequestId = null;
      state.isTrafficLightFlow = false;
    },
    resetTCUMakeNewType: (state) => {
      state.createNewAppointment.selectedTCUMakeNewType = null;
    },
    resetTCUSlots: (state) => {
      state.createNewAppointment.selectedTCUSlots = null;
      state.selectedSlot = null;
    },

    resetVcpPatientDetails: (state) => {
      state.vcpPatientDetails = null;
    },
    // reset video consult flow status
    resetVideoConsultFlow: (state) => {
      state.createNewAppointment.isAcuteVideoConsult = null;
      state.createNewAppointment.isSingleOrganizationVcService = null;
    },
    setAppointmentSubmit: (state, action: PayloadAction<Appointment>) => {
      state.rescheduleAndBookNext.appointmentSubmit = action.payload;
    },
    setAppointmentSystemParams: (state, action: PayloadAction<AppointmentSystemParamsResult>) => {
      state.systemParams = action.payload;
    },
    setAppointmentType: (state, action: PayloadAction<string>) => {
      state.appointmentType = action.payload;
    },
    // FinalConfirmMessage
    setFinalConfirmMessage: (state, action: PayloadAction<string>) => {
      state.finalConfirmMessage = action.payload;
    },
    //
    setAppointmentDateRange: (state, action: PayloadAction<AppointmentDateRange>) => {
      state.appointmentDateRange = action.payload;
    },
    setHsgTcuRequestId: (state, action: PayloadAction<number>) => {
      state.hsgTcuRequestId = action.payload;
    },

    setIsHideQueueReminder: (state, action: PayloadAction<boolean>) => {
      state.isHideQueueReminder = action.payload;
    },

    setIsTrafficLightFlow: (state, action: PayloadAction<boolean>) => {
      state.isTrafficLightFlow = action.payload;
    },

    setLinkedAppointmentMap: (
      state,
      action: PayloadAction<{ appointmentId: string; slot: AppointmentSlot | null }>
    ) => {
      const { appointmentId, slot } = action.payload;
      state.rescheduleAndBookNext.linkedAppointmentSlotMap[appointmentId] = slot;
    },
    setMregContactNoUpdatedQnos: (state, action: PayloadAction<string[]>) => {
      state.mregContactNoUpdatedQnos = action.payload;
    },
    setMregSuccessErrText: (state, action: PayloadAction<string>) => {
      state.mregSuccessErrText = action.payload;
    },
    setMregTodayAppointment: (state, action: PayloadAction<TodayAppointment>) => {
      state.mregTodayAppointment = action.payload;
    },
    // create new appointment
    setNewAppointment: (state, action: PayloadAction<Partial<CreateNewAppointment>>) => {
      state.createNewAppointment = {
        ...state.createNewAppointment,
        ...action.payload,
      };
    },
    // pre questionnaire form value
    setPreQuestionnaire: (state, action: PayloadAction<Partial<PreQuestionnaire>>) => {
      state.preQuestionnaire = {
        ...state.preQuestionnaire,
        ...action.payload,
      };
    },
    // Reschedule and book next
    setSelectedAppointment: (state, action: PayloadAction<Appointment>) => {
      state.selectedAppointment = action.payload;
    },
    setSelectedLinkedAppointment: (state, action: PayloadAction<Appointment>) => {
      state.rescheduleAndBookNext.selectedLinkedAppointment = action.payload;
    },
    /// Common
    setSelectedSlots: (state, action: PayloadAction<GroupedAppointmentSlots>) => {
      state.selectedSlot = action.payload;
    },
    // AODTH patient details
    setVcpPatientDetails: (state, action: PayloadAction<null | VcpPatientDetails>) => {
      state.vcpPatientDetails = action.payload;
    },
  },
});

export const { actions: appointmentAction } = appointmentSlice;

export default appointmentSlice.reducer;
